import { useEffect, useState } from 'react';

import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { EntityMetadataModel, ParameterMetadata, PrimitiveType } from '../../../../api/api';
import DialogHeaderActions from '../../../../components/DialogHeaderActions';
import { useClipboardComponent } from '../../../../common/hooks/useClipboardComponent';
import { usePersistedSetting } from '../../../../common/hooks/usePersistedSetting';
import { usePersistedSize } from '../../../../common/hooks/usePersistedSize';
import CopyToClipboardButton from '../../../../components/Clipboard/CopyToClipboardButton';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';
import { ObjectExpressionModel } from '../../Models/ObjectExpressionModel';
import { StringTemplateExpressionModel } from '../../Models/StringTemplateModel';
import { VariableScope } from '../../Models/VariableScopes/VariableScope';
import { resolveExpectedObjectType } from '../../Services/ExpectedObjectTypeResolver';
import TemplateEditorContent from '../Templates/TemplateEditorContent';
import ExpressionObjectPlaceholder from './ExpressionObjectPlaceholder';
import ExpressionPlaceholder from './ExpressionPlaceholder';

type ExpressionComposerDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	parameterMetadata: ParameterMetadata;
	expression: ExpressionModel | undefined;
	onExpressionChange?: (expression: ExpressionModel | undefined) => void;
	variableScope: VariableScope | undefined;
	expectedObjectType?: EntityMetadataModel;
	model?: Record<string, any>;
};

const DIALOG_SIZE_KEY = 'expressionComposerDialog.size';
const DIALOG_MAXIMIZED_KEY = 'expressionComposerDialog.maximized';

export default function ExpressionComposerDialog({
	open,
	setOpen,
	parameterMetadata,
	expression,
	onExpressionChange,
	variableScope,
	expectedObjectType,
	model,
}: ExpressionComposerDialogProps) {
	const handleClose = () => {
		setOpen(false);
	};
	const handleSave = () => {
		onExpressionChange?.(expressionPending);
		setOpen(false);
	};

	const [expressionPending, setExpressionPending] = useState<ExpressionModel | undefined>(expression);
	const [isMaximized, setIsMaximized] = usePersistedSetting(DIALOG_MAXIMIZED_KEY, false);
	const { ref: paperRef, size: storedSize } = usePersistedSize(DIALOG_SIZE_KEY, open);
	const clipboardExpression = useClipboardComponent<ExpressionModel>();

	const effectiveExpectedObjectType = resolveExpectedObjectType(parameterMetadata, expectedObjectType);
	const isObjectType = !parameterMetadata.expectedExpressionIsAssignable && (!!effectiveExpectedObjectType || parameterMetadata.expectedType?.type === PrimitiveType.Object);
	const isCollectionType = !parameterMetadata.expectedExpressionIsAssignable && parameterMetadata.expectedType?.typeWrapInCollection === true;
	const canComposeObject = isObjectType && !isCollectionType;
	const isComposingObject = canComposeObject && expressionPending?.Type === ExpressionTypes.Object;

	useEffect(() => {
		setExpressionPending(expression);
	}, [open, expression]);

	const handlePaste = () => {
		if (clipboardExpression) {
			try {
				// Validate that it's a valid expression by checking it has a Type property
				if (clipboardExpression.Type) {
					setExpressionPending(clipboardExpression);
				}
			} catch (error) {
				console.error('Failed to paste expression from clipboard:', error);
			}
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullScreen={isMaximized}
			fullWidth={true}
			maxWidth={false}
			PaperProps={{
				ref: paperRef,
				sx: isMaximized
					? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', resize: 'none', overflow: 'hidden' }
					: { width: storedSize.width, height: storedSize.height, maxWidth: '95vw', maxHeight: '90vh', resize: 'both', overflow: 'hidden' },
			}}
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
				Compose Expression of{' '}
				<Box component="span" sx={{ color: 'primary.main' }}>
					{parameterMetadata.displayName || parameterMetadata.name}
				</Box>
				<Box sx={{ flex: 1 }} />
				<DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized((value) => !value)} onClose={handleClose}>
					<CopyToClipboardButton title="Copy expression JSON to clipboard" edge={false} onClick={() => JSON.stringify(expressionPending, null, 2)} />
					<Tooltip title="Paste expression from clipboard">
						<span>
							<IconButton onClick={handlePaste} size="small" disabled={!clipboardExpression}>
								<ContentPasteIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</DialogHeaderActions>
			</DialogTitle>
			<DialogContent dividers sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch', px: 2, py: 1.5 }}>
				<DialogContentText>
					The <b>{parameterMetadata.name}</b> parameter is <b>{parameterMetadata.displayName}</b>
					{parameterMetadata.description && (
						<Typography display="block" variant="caption">
							{parameterMetadata.description}
						</Typography>
					)}
				</DialogContentText>
				{canComposeObject && (
					<Stack direction="row" spacing={1}>
						<Button variant={isComposingObject ? 'outlined' : 'contained'} onClick={() => isComposingObject && setExpressionPending(undefined)}>
							Select expression
						</Button>
						<Button
							variant={isComposingObject ? 'contained' : 'outlined'}
							onClick={() => !isComposingObject && setExpressionPending({ Type: ExpressionTypes.Object, Properties: {} } as ObjectExpressionModel)}
						>
							Compose object
						</Button>
					</Stack>
				)}

				{!parameterMetadata.expectedExpressionIsAssignable && expressionPending?.Type === ExpressionTypes.StringTemplate ? (
					<TemplateEditorContent
						expression={expressionPending}
						onExpressionChange={setExpressionPending}
						format={(expressionPending as StringTemplateExpressionModel).Format ?? 'Text'}
						parameterMetadata={parameterMetadata}
						variableScope={variableScope}
						allowPlaceholderExpressionEditing={true}
					/>
				) : !isComposingObject ? (
					<ExpressionPlaceholder
						variableScope={variableScope}
						parameterMetadata={parameterMetadata}
						expression={expressionPending}
						onExpressionChange={setExpressionPending}
						isEditable={true}
						expectedObjectType={effectiveExpectedObjectType}
						model={model}
						preferExpressionPicker={canComposeObject}
					/>
				) : (
					<ExpressionObjectPlaceholder
						expression={expressionPending as ObjectExpressionModel}
						onExpressionChange={setExpressionPending}
						variableScope={variableScope}
						expectedObjectType={effectiveExpectedObjectType}
					/>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button onClick={handleSave} variant="contained" color="primary">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
